
import { useState } from "react";
import { Video, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnalysisUploader } from "@/components/analysis";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";

const VideoAnalysis = () => {
  const [results, setResults] = useState<{
    results: {
      type: string;
      level: ResultLevel;
      score: number;
      details: string;
    }[];
    overallStatus: ResultLevel;
  } | null>(null);

  const handleAnalyze = async (file: File | string) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        const mockResults = {
          results: [
            {
              type: "Deepfake Detection",
              level: "suspicious" as ResultLevel,
              score: 65,
              details: "Some facial inconsistencies detected that may indicate deepfake technology.",
            },
            {
              type: "Temporal Consistency",
              level: "suspicious" as ResultLevel,
              score: 58,
              details: "Minor inconsistencies in motion and physics across frames.",
            },
            {
              type: "Audio-Visual Sync",
              level: "safe" as ResultLevel,
              score: 25,
              details: "Audio appears properly synchronized with visual content.",
            },
            {
              type: "Digital Artifacts",
              level: "suspicious" as ResultLevel,
              score: 52,
              details: "Some compression artifacts that could be masking manipulation.",
            },
          ],
          overallStatus: "suspicious" as ResultLevel,
        };
        
        setResults(mockResults);
        resolve();
      }, 2000);
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Video Analysis</h1>
        <p className="text-muted-foreground">
          Detect deepfakes, manipulated videos, and synthetic content
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <AnalysisUploader
            title="Video Analysis"
            description="Upload a video file to detect potential manipulation"
            acceptedFileTypes=".mp4,.mov,.avi,.webm"
            icon={<Video className="h-5 w-5 text-fraudeye-accent" />}
            onAnalyze={handleAnalyze}
            allowText={false}
          />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                Our video analysis uses multiple detection techniques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium">Deepfake Detection</h3>
                <p className="text-sm text-muted-foreground">
                  Identifies facial manipulation, inconsistent blinking, and other signs of synthetic face generation.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Temporal Consistency</h3>
                <p className="text-sm text-muted-foreground">
                  Analyzes motion consistency across frames to detect splicing and other time-based manipulations.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Audio-Visual Sync</h3>
                <p className="text-sm text-muted-foreground">
                  Detects misalignment between lip movements and spoken words, common in manipulated videos.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Digital Artifacts</h3>
                <p className="text-sm text-muted-foreground">
                  Identifies unusual compression patterns, boundary inconsistencies, and other technical artifacts.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          {results ? (
            <AnalysisResults {...results} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Video className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No Analysis Results Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Upload a video to begin analysis. Results will appear here once processing is complete.
                  </p>
                  <Alert variant="default" className="max-w-md border-fraudeye-accent/50 bg-fraudeye-accent/10">
                    <AlertTriangle className="h-4 w-4 text-fraudeye-accent" />
                    <AlertTitle>Tip</AlertTitle>
                    <AlertDescription>
                      Video analysis may take longer depending on file size and length.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Button className="w-full" variant="outline">
              Export Results
            </Button>
            <Button className="w-full bg-fraudeye-accent hover:bg-fraudeye-highlight">
              New Analysis
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoAnalysis;
